import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { selectIdentity } from "../../../selectors/userSelectors";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import {
  messageFromError,
  parseDrfFieldErrors,
} from "../../../shared/api/drfErrors";
import {
  fetchCompany,
  fetchMyCompany,
} from "../../companyWorkspace/api/companyWorkspace";
import {
  isActiveMembership,
} from "../../companyWorkspace/lib/companyLabels";
import {
  createScopeGate,
  sameActorId,
  sameCompanyId,
} from "../../companyWorkspace/lib/scopeSession";
import {
  FOCUS_REVALIDATION_UNRESOLVED_MESSAGE,
  openBackgroundRevalidation,
  settleRevalidationWrites,
  useCompanyFocusRevalidation,
} from "../../companyWorkspace/lib/companyFocusRevalidation";
import CompanyPageHeader from "../../companyWorkspace/ui/CompanyPageHeader";
import {
  buildLocationApiPayload,
  validateLocationCanonical,
  validateLocationCoordinates,
  validateLocationStructured,
} from "../../geo/lib/locationGeo";
import LandParcelsEditor from "../../landParcels/widgets/LandParcelsEditor";
import EditSectionType from "../../editRealty/widgets/EditSectionType";
import EditSectionLocation from "../../editRealty/widgets/EditSectionLocation";
import EditSectionSpecs from "../../editRealty/widgets/EditSectionSpecs";
import EditSectionDescription from "../../editRealty/widgets/EditSectionDescription";
import EditSectionMedia from "../../editRealty/widgets/EditSectionMedia";
import {
  SUBTYPE_BY_PROPERTY_TYPE,
  buildCommercialPayload,
  buildInitialForm,
  buildLandPayload,
  buildResidentialPayload,
  coerceNullable,
  collectSpecsFieldIssues,
  formFromPropertyDetail,
  validateStep1ForDraft,
} from "../../createRealty/lib/wizardContract";

import { createCompanyPropertyTransport } from "../api/companyProperties";
import {
  buildCorporatePropertyPatchBody,
  emptyCoreFormFromProperty,
  hasConfidentialKey,
  isCorporatePropertiesAvailable,
  propertyDisplayTitle,
} from "../lib/propertyLabels";
import {
  createAuthorityBoundLandParcelsApi,
  createAuthorityBoundMediaApi,
  membershipGenerationOf,
} from "../lib/authorityBoundTransport";

const COMPANY_D1_FIELD_ERROR_KEYS = [
  "title",
  "description_public",
  "description_private",
  "property_type",
  "deal_type",
  "price",
  "currency",
  "video_url",
  "canonical_region",
  "canonical_settlement",
  "local_area",
  "address_object_id",
  "manual_address_object",
  "building_number",
  "legacy_address",
  "residential_complex",
  "total_area_m2",
  "living_area_m2",
  "kitchen_area_m2",
  "rooms",
  "floor",
  "total_floors",
  "land_area_m2",
  "area_m2",
  "latitude",
  "longitude",
  "cooperation_terms",
];

const dismissFieldErrorsForKeys = (setFieldErrors, keys) => {
  if (!keys || !keys.length) return;
  setFieldErrors((prev) => {
    let changed = false;
    const next = { ...prev };
    for (const key of keys) {
      if (next[key] !== undefined) {
        delete next[key];
        changed = true;
      }
    }
    return changed ? next : prev;
  });
};

const changedKeys = (prevObj, nextObj) => {
  const keys = new Set([
    ...Object.keys(prevObj || {}),
    ...Object.keys(nextObj || {}),
  ]);
  const out = [];
  for (const key of keys) {
    if ((prevObj || {})[key] !== (nextObj || {})[key]) out.push(key);
  }
  return out;
};

function buildCorporateCorePayload(core, baseline) {
  // Prefer explicit patch helper so omitted confidential keys stay omitted.
  return buildCorporatePropertyPatchBody(
    baseline,
    emptyCoreFormFromProperty({ ...baseline, ...core }),
  );
}

function CompanyPropertyEditContent() {
  const { companyId, propertyId } = useParams();
  const navigate = useNavigate();
  const identity = useSelector(selectIdentity);
  const actorId = identity?.userId ?? null;
  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);
  const busyOpRef = useRef(null);
  const membershipGenerationRef = useRef(null);
  const stateRef = useRef("loading");
  const formDirtyRef = useRef(false);
  const writesSuspendedRef = useRef(false);
  const revalidationTokenRef = useRef(0);
  const transport = useMemo(
    () => createCompanyPropertyTransport(companyId),
    [companyId],
  );

  const [company, setCompany] = useState(null);
  const [membership, setMembership] = useState(null);
  const [authorityActorId, setAuthorityActorId] = useState(null);
  const [property, setProperty] = useState(null);
  const [form, setForm] = useState(() => buildInitialForm());
  const [state, setState] = useState("loading");
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [landParcels, setLandParcels] = useState([]);
  const [childSessionKey, setChildSessionKey] = useState(0);
  const [formLocked, setFormLocked] = useState(false);
  const [writesSuspended, setWritesSuspended] = useState(false);
  stateRef.current = state;
  writesSuspendedRef.current = writesSuspended;

  const markFormDirty = () => {
    formDirtyRef.current = true;
  };

  const companyMatches =
    company != null && sameCompanyId(company.id, companyId);
  const actorMatches =
    authorityActorId != null && sameActorId(authorityActorId, actorId);
  const propertyMatches =
    property != null &&
    propertyId != null &&
    String(property.id) === String(propertyId);
  const authorityMatches = companyMatches && actorMatches;
  const available =
    authorityMatches && isCorporatePropertiesAvailable(company);
  const canEdit = Boolean(propertyMatches && property?.can_edit && available);

  const clearProtected = () => {
    setProperty(null);
    setForm(buildInitialForm());
    setFieldErrors({});
    setActionError(null);
    setLandParcels([]);
    setChildSessionKey((value) => value + 1);
  };

  const applyProperty = (detail) => {
    if (!detail || detail.id == null) {
      throw new Error("Не вдалося завантажити об'єкт");
    }
    setProperty(detail);
    const next = formFromPropertyDetail(detail);
    // Confidential description only when server projected it.
    if (!hasConfidentialKey(detail, "description_private")) {
      delete next.core.description_private;
    }
    setForm(next);
  };

  const load = useCallback(async ({ background = false } = {}) => {
    if (!companyId || !propertyId) {
      setState("not-found");
      return;
    }
    // R2-1: phase-1 read validates company/mine before identity bind; detail uses
    // a synchronized read opened after membershipGeneration is bound.
    let activeOp = scopeGate.beginRead({
      companyId,
      actorId,
      resourceId: propertyId,
    });
    const keepVisible = background && stateRef.current === "ready";
    const tokenAtStart = revalidationTokenRef.current;
    let backgroundToken = null;
    let revalidationConfirmed = false;
    if (keepVisible) {
      backgroundToken = openBackgroundRevalidation(
        revalidationTokenRef,
        writesSuspendedRef,
        setWritesSuspended,
      );
    } else {
      setState("loading");
      setError(null);
    }
    try {
      const [companyData, mine] = await Promise.all([
        fetchCompany(companyId),
        fetchMyCompany(),
      ]);
      if (!scopeGate.readStillValid(activeOp, { mounted: mountedRef.current })) {
        return;
      }
      const mineCompanyMatches =
        mine?.company?.id != null && sameCompanyId(mine.company.id, companyId);
      const mineMatches = mineCompanyMatches ? mine.membership : null;
      setCompany(companyData);
      setMembership(mineMatches);
      setAuthorityActorId(actorId);
      const generation = membershipGenerationOf(mineMatches);
      membershipGenerationRef.current = generation;
      scopeGate.bindIdentity({
        companyId,
        actorId,
        membershipGeneration: generation,
        resourceId: propertyId,
      });
      if (!mineCompanyMatches || !isActiveMembership(companyData, mineMatches)) {
        clearProtected();
        setFormLocked(true);
        setState("denied");
        return;
      }
      if (!isCorporatePropertiesAvailable(companyData)) {
        clearProtected();
        setFormLocked(true);
        setState("unavailable");
        return;
      }
      const syncedOp = scopeGate.beginRead({
        companyId,
        actorId,
        resourceId: propertyId,
        membershipGeneration: generation,
      });
      activeOp = syncedOp;
      const detail = await transport.fetchPropertyDetail(propertyId);
      if (!scopeGate.readStillValid(syncedOp, { mounted: mountedRef.current })) {
        return;
      }
      if (!detail?.can_edit) {
        clearProtected();
        setFormLocked(true);
        setState("denied");
        return;
      }
      if (keepVisible && formDirtyRef.current) {
        setProperty(detail);
      } else {
        applyProperty(detail);
        formDirtyRef.current = false;
      }
      setFormLocked(false);
      revalidationConfirmed = true;
      setActionError((current) =>
        current === FOCUS_REVALIDATION_UNRESOLVED_MESSAGE ? null : current,
      );
      setState("ready");
    } catch (err) {
      if (!scopeGate.readStillValid(activeOp, { mounted: mountedRef.current })) {
        return;
      }
      const status = err?.response?.status;
      if (keepVisible && status !== 404 && status !== 403) {
        setActionError(FOCUS_REVALIDATION_UNRESOLVED_MESSAGE);
        return;
      }
      clearProtected();
      if (status === 404) setState("not-found");
      else if (status === 403) setState("denied");
      else {
        setError(messageFromError(err));
        setState("error");
      }
    } finally {
      settleRevalidationWrites({
        keepVisible: keepVisible,
        confirmed: revalidationConfirmed,
        token: backgroundToken,
        tokenAtStart,
        tokenRef: revalidationTokenRef,
        writesSuspendedRef,
        setWritesSuspended,
      });
    }
  }, [actorId, companyId, propertyId, scopeGate, transport]);

  useEffect(() => {
    mountedRef.current = true;
    membershipGenerationRef.current = null;
    scopeGate.bindIdentity({
      companyId,
      actorId,
      membershipGeneration: null,
      resourceId: propertyId,
    });
    busyOpRef.current = null;
    clearProtected();
    setCompany(null);
    setMembership(null);
    setAuthorityActorId(null);
    load();
    return () => {
      mountedRef.current = false;
      scopeGate.invalidate();
      busyOpRef.current = null;
    };
  }, [companyId, actorId, propertyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useCompanyFocusRevalidation({
    enabled: Boolean(companyId && propertyId),
    busy,
    onRevalidate: () => load({ background: true }),
  });

  const handleSave = async () => {
    if (!canEdit || formLocked || writesSuspendedRef.current || busyOpRef.current) return;
    const locIssues = {
      ...validateLocationCanonical(form.location),
      ...validateLocationStructured(form.location),
      ...validateLocationCoordinates(form.location),
    };
    const coreIssues = validateStep1ForDraft(form.core);
    const specsIssues = collectSpecsFieldIssues(form);
    const issues = { ...coreIssues, ...locIssues, ...specsIssues };
    if (Object.keys(issues).length) {
      setFieldErrors(issues);
      return;
    }
    const op = scopeGate.beginMutation({
      companyId,
      actorId,
      resourceId: propertyId,
      membershipGeneration: membershipGenerationRef.current,
    });
    busyOpRef.current = op;
    setBusy(true);
    setActionError(null);
    setFieldErrors({});
    try {
      const coreBody = buildCorporateCorePayload(form.core, property);
      if (Object.keys(coreBody).length) {
        if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        await transport.patchPropertyCore(propertyId, coreBody);
        if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
      }
      const locationBody = buildLocationApiPayload(
        form.location,
        coerceNullable,
      );
      if (locationBody) {
        if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        await transport.putPropertyLocation(propertyId, locationBody);
        if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
      }
      const subtype = SUBTYPE_BY_PROPERTY_TYPE[form.core.property_type];
      if (subtype === "residential") {
        if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        await transport.putPropertyResidential(
          propertyId,
          buildResidentialPayload(form.residential),
        );
        if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
      } else if (subtype === "land") {
        if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        await transport.putPropertyLand(
          propertyId,
          buildLandPayload(form.land),
        );
        if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
      } else if (subtype === "commercial") {
        if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        await transport.putPropertyCommercial(
          propertyId,
          buildCommercialPayload(form.commercial),
        );
        if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
      }
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const detail = await transport.fetchPropertyDetail(propertyId);
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      applyProperty(detail);
      toast.success("Зміни збережено.");
      navigate(`/companies/${companyId}/properties/${propertyId}`);
    } catch (err) {
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const status = err?.response?.status;
      if (status === 403 || status === 404) {
        clearProtected();
        setFormLocked(true);
        setState(status === 404 ? "not-found" : "denied");
        return;
      }
      setFieldErrors(parseDrfFieldErrors(err, COMPANY_D1_FIELD_ERROR_KEYS) || {});
      setActionError(messageFromError(err));
    } finally {
      if (busyOpRef.current === op) {
        busyOpRef.current = null;
        setBusy(false);
      }
    }
  };


  const guardedMediaApi = useMemo(() => {
    const api = createAuthorityBoundMediaApi({
      transport,
      scopeGate,
      getAuthority: () => ({
        companyId,
        actorId,
        resourceId: propertyId,
        membershipGeneration: membershipGenerationRef.current,
      }),
      mountedRef,
      onAuthorityLoss: (status) => {
        clearProtected();
        setFormLocked(true);
        setState(status === 404 ? "not-found" : "denied");
      },
    });
    const guard = (fn) => (...args) => {
      if (writesSuspendedRef.current) {
        return Promise.reject(new Error(FOCUS_REVALIDATION_UNRESOLVED_MESSAGE));
      }
      return fn(...args);
    };
    return {
      ...api,
      uploadPropertyImages: guard(api.uploadPropertyImages),
      patchPropertyImage: guard(api.patchPropertyImage),
      deletePropertyImage: guard(api.deletePropertyImage),
      reorderPropertyImages: guard(api.reorderPropertyImages),
    };
  }, [actorId, companyId, propertyId, scopeGate, transport]);

  const guardedLandParcelsApi = useMemo(
    () =>
      createAuthorityBoundLandParcelsApi({
        transport,
        scopeGate,
        getAuthority: () => ({
          companyId,
          actorId,
          resourceId: propertyId,
          membershipGeneration: membershipGenerationRef.current,
        }),
        mountedRef,
        onAuthorityLoss: (status) => {
          clearProtected();
          setFormLocked(true);
          setState(status === 404 ? "not-found" : "denied");
        },
        fetchDetail: () => transport.fetchPropertyDetail(propertyId),
        onRefresh: async (detail) => {
          if (!mountedRef.current || !detail) return;
          applyProperty(detail);
        },
      }),
    [actorId, companyId, propertyId, scopeGate, transport],
  );

  return (
    <div
      className="clients-page feature-company-workspace feature-company-properties edit-property-page add-property-page"
      id="Csection"
      tabIndex="-1"
    >
      <div className="container">
        <CompanyPageHeader
          title={
            propertyMatches
              ? `Редагування · ${propertyDisplayTitle(property)}`
              : "Редагування об'єкта"
          }
          subtitle="Корпоративне редагування · Компанія"
          company={authorityMatches ? company : null}
          membership={authorityMatches ? membership : null}
          companyId={companyId}
          activeNav="properties"
          actions={
            <Link
              to={`/companies/${companyId}/properties/${propertyId || ""}`}
              className="action-btn action-btn--ghost"
            >
              До картки
            </Link>
          }
        />

        {state === "loading" || formLocked ? (
          <div className="feature-company-workspace__state">
            <Spinner />
          </div>
        ) : null}
        {state === "unavailable" ? (
          <EmptyState
            title="Корпоративні об'єкти недоступні"
            description="Редагування об'єктів компанії вимкнене."
          />
        ) : null}
        {state === "denied" ? (
          <EmptyState
            title="Немає доступу"
            description="Немає права редагувати цей корпоративний об'єкт."
          />
        ) : null}
        {state === "not-found" ? (
          <EmptyState
            title="Не знайдено"
            description="Об'єкт компанії не знайдено або приховано."
          />
        ) : null}
        {state === "error" ? (
          <EmptyState
            title="Помилка"
            description={error || "Спробуйте ще раз."}
            primaryAction={
              <button type="button" className="action-btn" onClick={load}>
                Повторити
              </button>
            }
          />
        ) : null}

        {state === "ready" && canEdit && propertyMatches ? (
          <div className="edit-property-page__body">
            {actionError ? (
              <p className="feature-company-workspace__error" role="alert">
                {actionError}
              </p>
            ) : null}
            <EditSectionType
              values={form.core}
              onChange={(core) => {
                markFormDirty();
                setForm((prev) => {
                  dismissFieldErrorsForKeys(
                    setFieldErrors,
                    changedKeys(prev.core, core),
                  );
                  return { ...prev, core };
                });
              }}
              fieldErrors={fieldErrors}
              propertyTypeLocked
            />
            <EditSectionLocation
              propertyId={propertyId}
              values={form.location}
              onChange={(location) => {
                markFormDirty();
                setForm((prev) => {
                  dismissFieldErrorsForKeys(
                    setFieldErrors,
                    changedKeys(prev.location, location),
                  );
                  return { ...prev, location };
                });
              }}
              fieldErrors={fieldErrors}
            />
            <EditSectionSpecs
              propertyType={form.core.property_type}
              residential={form.residential}
              land={form.land}
              commercial={form.commercial}
              fieldErrors={fieldErrors}
              onChangeResidential={(residential) => {
                markFormDirty();
                setForm((prev) => {
                  dismissFieldErrorsForKeys(
                    setFieldErrors,
                    changedKeys(prev.residential, residential),
                  );
                  return { ...prev, residential };
                });
              }}
              onChangeLand={(land) => {
                markFormDirty();
                setForm((prev) => {
                  dismissFieldErrorsForKeys(
                    setFieldErrors,
                    changedKeys(prev.land, land),
                  );
                  return { ...prev, land };
                });
              }}
              onChangeCommercial={(commercial) => {
                markFormDirty();
                setForm((prev) => {
                  dismissFieldErrorsForKeys(
                    setFieldErrors,
                    changedKeys(prev.commercial, commercial),
                  );
                  return { ...prev, commercial };
                });
              }}
            />
            <LandParcelsEditor
              propertyId={propertyId}
              propertyType={form.core.property_type}
              initialParcels={landParcels}
              onParcelsChange={setLandParcels}
              key={`parcels-${childSessionKey}-${propertyId}`}
              api={guardedLandParcelsApi}
              disabled={busy || formLocked || writesSuspended}
            />
            <EditSectionMedia
              property={property}
              key={`media-${childSessionKey}-${propertyId}`}
              mediaApi={guardedMediaApi}
              locked={writesSuspended}
              videoUrl={form.core.video_url}
              onVideoUrlChange={(video_url) => {
                markFormDirty();
                setForm((prev) => ({
                  ...prev,
                  core: { ...prev.core, video_url },
                }));
              }}
              onMediaChanged={async () => {
                const readOp = scopeGate.beginRead({
                  companyId,
                  actorId,
                  resourceId: propertyId,
                  membershipGeneration: membershipGenerationRef.current,
                });
                const detail = await transport.fetchPropertyDetail(propertyId);
                if (
                  !scopeGate.readStillValid(readOp, {
                    mounted: mountedRef.current,
                  })
                ) {
                  return;
                }
                applyProperty(detail);
              }}
            />
            <EditSectionDescription
              values={form.core}
              onChange={(core) => {
                markFormDirty();
                setForm((prev) => {
                  dismissFieldErrorsForKeys(
                    setFieldErrors,
                    changedKeys(prev.core, core),
                  );
                  return { ...prev, core };
                });
              }}
              fieldErrors={fieldErrors}
            />
            <div className="clients-toolbar__row">
              <button
                type="button"
                className="action-btn action-btn--accent"
                disabled={busy || formLocked || writesSuspended}
                aria-busy={busy}
                onClick={handleSave}
              >
                Зберегти
              </button>
              <Link
                to={`/companies/${companyId}/properties/${propertyId}`}
                className="action-btn action-btn--ghost"
              >
                Скасувати
              </Link>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default CompanyPropertyEditContent;
